<template>
	<output :class="rootClass" role="slider">
		<span
			v-if="showClear"
			tabindex="-1"
			class="b-rating-star b-rating-star-clear flex-grow-1"
			:style="readonly ? null : { cursor: 'pointer' }"
			@click="clearRating">
			<span class="b-rating-icon">
				<BIcon icon="x" />
			</span>
		</span>
		<span
			v-for="i in starsCount"
			:key="i"
			:class="starClass(i)"
			:style="readonly ? null : { cursor: 'pointer' }"
			@click="setRating(i)">
			<span class="b-rating-icon">
				<BIcon :icon="getStarIcon(i)" :style="starStyle(i)" />
			</span>
		</span>
	</output>
</template>

<script>
	export default {
		name: 'BFormRating',
		// Written for Vue 3 (modelValue v-model, class and style in $attrs), so skip the Vue 2 compat rewrites
		compatConfig: { MODE: 3 },
		props: {
			modelValue: { type: [Number, String, null], default: null },
			value: { type: [Number, String, null], default: null },
			stars: { type: [Number, String], default: 5 },
			readonly: { type: Boolean, default: false },
			inline: { type: Boolean, default: false },
			size: { type: String, default: null },
			color: { type: String, default: null },
			variant: { type: String, default: null },
			state: { type: [Boolean, null], default: null },
			noBorder: { type: Boolean, default: false },
			showClear: { type: Boolean, default: false },
		},
		emits: ['update:modelValue', 'change'],
		computed: {
			currentValue() {
				const v = this.modelValue !== null ? this.modelValue : this.value;
				return v == null ? 0 : Number(v);
			},
			starsCount() {
				return Number(this.stars) || 5;
			},
			activeColor() {
				if (this.color) {
					return this.color || '#6c6c6c';
				}

				switch (this.variant) {
					case 'warning':
						return '#f8ac59';
					case 'inherit':
						return '#343a40';
					default:
						return '#6c6c6c';
				}
			},
			emptyColor() {
				switch (this.variant) {
					case 'warning':
						return '#f8ac59';
					default:
						return '#a9aeb4';
				}
			},
			rootClass() {
				return [
					'b-rating',
					'b-form-rating',
					'form-control',
					'align-items-center',
					this.inline ? 'd-inline-flex' : 'd-flex',
					this.size === 'sm' ? 'form-control-sm' : null,
					this.size === 'lg' ? 'form-control-lg' : null,
					this.noBorder ? 'border-0' : null,
					this.state === true ? 'is-valid' : null,
					this.state === false ? 'is-invalid' : null,
				].filter(Boolean);
			},
			starClass() {
				return (i) =>
					[
						'b-rating-star',
						'flex-grow-1',
						i <= this.currentValue ? 'b-rating-star-filled' : 'b-rating-star-empty',
						'mr-1',
					].filter(Boolean);
			},
		},
		methods: {
			starStyle(i) {
				const isFilled = i <= this.currentValue;
				return {
					color: isFilled ? this.activeColor : this.emptyColor,
				};
			},
			clearRating() {
				if (this.readonly) {
					return;
				}
				this.$emit('update:modelValue', null);
				this.$emit('change', null);
			},
			setRating(n) {
				if (this.readonly) return;
				this.$emit('update:modelValue', n);
				this.$emit('change', n);
			},
			getStarIcon(i) {
				const rating = Number(this.currentValue);
				const fullStars = Math.floor(rating);
				const decimal = rating - fullStars;

				if (i <= fullStars) {
					return 'star-fill';
				}

				if (i === fullStars + 1 && decimal >= 0.5) {
					return 'star-half';
				}

				return 'star';
			},
		},
	};
</script>
